import { Check, Copy } from "lucide-react";
import { useEffect, useState } from "react";

export const VersionDisplay = () => {
  const [version, setVersion] = useState<string>("");
  const [copied, setCopied] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    fetch("/version.txt")
      .then((response) => response.text())
      .then((version) => setVersion(version.trim()))
      .catch((error) => {
        console.error("Error fetching version:", error);
        setErrors((prev) => [...prev, `Error fetching version: ${error.message}`]);
      });
  }, []);

  const copyToClipboard = async () => {
    try {
      // Get console errors if any exist
      const consoleErrors = errors.length > 0 ? "\n\nErrors:\n" + errors.join("\n") : "";

      // Get any console logs
      const consoleOutput = (window as any).__console_output || [];
      const consoleLogs = consoleOutput.length > 0 ? "\n\nConsole Output:\n" + consoleOutput.join("\n") : "";

      // Combine version with any console errors and logs
      const textToCopy = `Version: ${version}${consoleErrors}${consoleLogs}`;

      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Error copying to clipboard:", error);
      setErrors((prev) => [...prev, `Error copying to clipboard: ${error.message}`]);
    }
  };

  // Listen for console errors
  useEffect(() => {
    const errorHandler = (event: ErrorEvent) => {
      setErrors((prev) => [...prev, `${event.message} (${event.filename}:${event.lineno})`]);
    };

    // Override console methods to capture logs
    if (!(window as any).__console_output) {
      (window as any).__console_output = [];
      const originalConsole = { ...console };

      ["log", "error", "warn", "info"].forEach((method) => {
        console[method] = (...args) => {
          originalConsole[method](...args);
          (window as any).__console_output.push(`[${method.toUpperCase()}] ${args.join(" ")}`);
        };
      });
    }

    window.addEventListener("error", errorHandler);
    return () => window.removeEventListener("error", errorHandler);
  }, []);

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-zinc-400">Version: {version}</span>
      <button
        onClick={copyToClipboard}
        className="p-1 hover:bg-zinc-700 rounded-full transition-colors duration-300"
        title="Copy version, error logs, and console output"
      >
        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-zinc-400" />}
      </button>
    </div>
  );
};
